import { IconButton, Stack, Tooltip } from "@mui/material";
import { useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import PlayCircleIcon from "@mui/icons-material/PlayCircle";
import AutoFixHighIcon from "@mui/icons-material/AutoFixHigh";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import CableIcon from "@mui/icons-material/Cable";
import { selectUserData } from "../../../../features/userSlice";
import { CheckPermissions } from "../../../../logic/Admin/Permissions/CheckPermission/CheckPermissions";
import { getAutomaticConfigureAction } from "./utils/useAutomaticConfigure";
import { normalizeBrandStatus } from "./utils/helpersOfBrands";

interface Props {
  row: any;
  onRunBrand: (row: any) => void;
  onReconfigure: (row: any) => void;
  onTryToConnect: (row: any) => void;
}

// Icon set per status, per spec: Brand Overview is always present; the rest
// branch on row.status —
//   Synced        -> Run Brand
//   Config Issue  -> Automatic Configure + Manual Configure
//   Account Issue -> Account Settings (V1)
//   Not Connected -> Try to Connect
// Automatic/Manual Configure reuse the existing "configure_new_brand"
// permission gate from BrandConfigurationReportTableData.tsx.
// Shared across every Brands tab (All Brands, Needs Configuration) — the icon
// set only depends on row.status, not which tab it's rendered from.
const BrandsQuickActions = ({ row, onRunBrand, onReconfigure, onTryToConnect }: Props) => {
  const location = useLocation();
  const userData = useSelector(selectUserData);

  const status = normalizeBrandStatus(row?.status);

  const hasConfigurePermission = CheckPermissions(
    userData?.data?.products?.partners,
    "data_intregrations",
    "configure_new_brand",
  );

  const hasBrandDataOverviewPermission = CheckPermissions(
    userData?.data?.products?.partners,
    "data_intregrations",
    "brand_data_overview",
  );

  const handleOverview = () => {
    // Brand Overview now lives under the Brands module (operations/brands/...)
    // — same BrandConfigurationOverview component, reached via a dedicated
    // Brands-module route (BrandOverviewScreen.tsx) instead of the legacy
    // brand_configuration_report one, which is untouched.
    // Start from the Brands screen's current search params (tab, sort, page,
    // filters, ...) so returning to Brands can restore them.
    // Opened in a new browser tab so the Brands list stays exactly as the
    // user left it in the original tab.
    const params = new URLSearchParams(location.search);
    params.set("brand_id", row.brand_id ?? "");
    window.open(
      `/operations/brands/${encodeURIComponent(
        row.brand_name ?? "",
      )}?${params.toString()}`,
      "_blank",
    );
  };

  return (
    <Stack direction="row">
      <Tooltip title="Brand Overview">
        <IconButton
          size="small"
          color="secondary"
          data-tracker="784"
          onClick={handleOverview}
        >
          <DescriptionOutlinedIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      {status === "synced" && (
        <Tooltip title="Run Brand">
          <IconButton
            size="small"
            color="secondary"
            onClick={() => onRunBrand(row)}
          >
            <PlayCircleIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {status === "config_issue" && (
        <>
          {(() => {
            const action = getAutomaticConfigureAction(row);
            const isWorkInProgress = action.key === "work_in_progress";
            return (
              <Tooltip title={action.label}>
                <span>
                  <IconButton
                    size="small"
                    color="secondary"
                    data-tracker="628"
                    disabled={!hasConfigurePermission || isWorkInProgress}
                    onClick={() => onReconfigure(row)}
                  >
                    <AutoFixHighIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            );
          })()}
          {/* Temporarily disabled — the Manual Configure screen it links to
              (added under GR-21999) hasn't been specced or requested for the
              Brands status work, so the icon stays visible but inert until
              that screen is signed off. To restore: drop `disabled`, re-add
              color="secondary", and link it back with react-router's Link
              (component={Link} to="/operations/brands/manual_configuration").
              The screen and its route are left in place, untouched. */}
          <Tooltip title="Manual Configure">
            <span>
              <IconButton size="small" disabled>
                <BuildOutlinedIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </>
      )}

      {status === "account_issue" && (
        <Tooltip title="Account Settings">
          <IconButton
            size="small"
            color="secondary"
            onClick={() =>
              window.open(
                `https://etloptival.com/admin/business_development/brandaccounts/${row.account_id}/change/`,
                "_blank",
              )
            }
          >
            <TuneIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      )}

      {status === "not_connected" && (
        // POSTs [brand_id] to try_to_connect/. Gated on the
        // "brand_data_overview" permission — disabled (not hidden) without it,
        // same as Automatic Configure above.
        <Tooltip title="Try to Connect">
          <span>
            <IconButton
              size="small"
              color="secondary"
              disabled={!hasBrandDataOverviewPermission}
              onClick={() => onTryToConnect(row)}
            >
              <CableIcon fontSize="small" />
            </IconButton>
          </span>
        </Tooltip>
      )}
    </Stack>
  );
};

export default BrandsQuickActions;
